<script>
   /*************************************************************/
   /* AppPlots global component for plots routing               */
   /* -------------------------------------------               */
   /* show list of plots and let user to select one, and        */
   /* interact with it.                                         */
   /*************************************************************/


   // import control components
   import { untrack } from 'svelte';
   import { Widget } from 'svelte-controls-basic';

   let {
      plots,                  // JSON with plot components, labels and scope
      props,                  // JSON with main properties, including model, dataset and results
      controls,               // JSON with plot controls as Widget properties
      selobjText = 'Click to select an object...',
      selvarText = 'Make rectangular selection of variable to exclude ...',
      seloptText = 'Click to select an option...',
      clearText = 'Click anywhere to remove selection...',
      params = $bindable(),   // array with JSONs containing selObject plot parameters
   } = $props();


   // app state parameters (objects are selected individually, variables - as array)
   let selItems = $state({objects: -1, variables: [], options: []});

   // which plot the user has asked for. untrack marks the capture-at-mount as deliberate: the
   // first plot is only the one to open on, and the choice belongs to the user from then on
   let selPlotId = $state(untrack(() => Object.keys(plots)[0]));

   // the plot actually shown. the set of plots is not fixed — ddsimca's test tab builds it from
   // the number of members and strangers, which can change while this component stays mounted,
   // so the plot the user picked may no longer be there and the first one left is shown instead.
   // 'selPlotId' deliberately keeps the old id: if that plot comes back, so does the user's choice
   const currentId = $derived(plots[selPlotId] ? selPlotId : Object.keys(plots)[0]);

   // mark object as selected
   function selectObject(ind, map) {
      selItems.objects = map ? map[ind] : ind;
   }

   // remove the selection
   function unselectObject() {
      selItems.objects = -1;
   }

   // mark variable as selected
   function selectVariable(ind) {
      selItems.variables = ind;
   }

   // remove the selection
   function unselectVariable() {
      selItems.variables = [];
   }

   // mark variable as selected
   function selectOption(v) {
      selItems.options = v;
   }

   // remove the selection
   function unselectOption() {
      selItems.options = [];
   }

   // callbacks for each scope
   const appCallbacks = {
      objects: {select: selectObject, unselect: unselectObject},
      variables: {select: selectVariable, unselect: unselectVariable},
      options: {select: selectOption, unselect: unselectOption},
      stats: {}
   };

   // derived elements ('plots' is empty only if an app gives us nothing to show)
   const selPlot = $derived(currentId === undefined ? null : plots[currentId]); // selected plot
   const PlotComponent = $derived(selPlot ? selPlot.component : null);          // its component
</script>

<div class="plot-panel">

   <!-- column with current plot. everything here reads 'selPlot', so it is all kept behind the
        one check rather than each line guarding itself -->
   {#if selPlot}
   <div class="plot-area">
      <div class="plot">
      <PlotComponent
         params={params[currentId]}
         callbacks={appCallbacks[selPlot.scope]}
         selected={selItems[selPlot.scope]}
         {props}
      />
      </div>
      <div class="plot-select-msg">
      {#if selPlot.scope === 'objects' && params[currentId].plotType !== 'l' && selobjText.length > 0}
         {#if selItems[selPlot.scope] < 0}
         <div class="msg-select">{selobjText}</div>
         {:else}
         <div class="msg-select">{clearText}</div>
         {/if}
      {/if}
      {#if selPlot.scope === 'variables' && params[currentId].plotType !== 'l' && selvarText.length > 0}
         {#if selItems[selPlot.scope].length === 0}
         <div class="msg-select">{selvarText}</div>
         {:else}
         <div class="msg-select">{clearText}</div>
         {/if}
      {/if}
      {#if selPlot.scope === 'options' && params[currentId].plotType !== 'l' && seloptText.length > 0}
         {#if selItems[selPlot.scope].length === 0}
         <div class="msg-select">{seloptText}</div>
         {:else}
         <div class="msg-select">{clearText}</div>
         {/if}
      {/if}
      </div>
   </div>
   {/if}

   <!-- column with plot selectors and current plot settings -->
   <div class="plot-controls-area">
      {#each Object.keys(plots) as plotId (plotId)}

         {#if selPlot && currentId === plotId}

            <Widget title={selPlot.label} labelWidth={12} options={controls[currentId]} bind:value={params[currentId]}/>

            {#if selPlot.scope === 'objects' && selPlot.buttons && selItems.objects >= 0}
            <div class="selected-buttons">
            {@render selPlot.buttons(selItems.objects)}
            </div>
            {/if}

            {#if selPlot.scope === 'variables' && selPlot.buttons && selItems.variables.length > 0}
            <div class="selected-buttons">
            {@render selPlot.buttons(selItems.variables)}
            </div>
            {/if}

            {#if selPlot.scope === 'options' && selPlot.buttons && selItems.options.length > 0}
            <div class="selected-buttons">
            {@render selPlot.buttons(selItems.options)}
            </div>
            {/if}

         {:else}
         <!-- tabindex="0": Safari's default Tab skips a button without one -->
         <button class="plot-selector plot-selector-{plotId}" onclick={() => selPlotId = plotId}
            tabindex="0">{plots[plotId].label}</button>
         {/if}
      {/each}

   </div>
</div>


<style>
   .selected-buttons > :global(.control-element) {
      background: #fff0c8;
   }

   /* the row keeps its colour under the pointer. without this the rule above only ties
      svelte-controls-basic's own ':hover' at (0,3,0) and the winner is decided by order, which
      is not the same in dev and in the build - the library's css lands after ours in dev and
      before it in the bundle */
   .selected-buttons > :global(.control-element:hover) {
      background: #fff0c8;
   }

   /* spacing is positional - a button only needs room from the one before it */
   .selected-buttons > :global(.control-element button:not(:first-of-type)) {
      margin-left: 5px;
   }

   /* the colour is not positional: a button is red because it takes something out of the model,
      not because it happens to be second. the apps mark those with 'destructive', so a lone
      'remove' looks the same as one standing next to a 'plot' button. ':global' is needed
      because both the class and the element come from svelte-controls-basic */
   .selected-buttons > :global(.control-element button.destructive) {
      color: white;
      border-color: crimson;
      background: crimson;
   }

   .selected-buttons > :global(.control-element button.destructive:hover) {
      background: #DC143C20;
      border-color: #DC143C80;
      color: crimson;
   }

   .plot-panel {
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      padding: 0;
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr minmax(270px, 30%);
   }

   .plot-area {
      padding: 1em;
      margin-top: 0.25rem;
      border-radius: 0.25em;
      background: #fefefe;
   }

   .plot-controls-area {
      padding: 0 0.25em 0 0.5em;
   }

   .plot-selector {
      cursor: pointer;
      color: #606060;
      background: #fffef480;
      box-sizing: border-box;
      padding: 0.5em 1em;
      margin: 0.25em 0;
      border-radius: 0.25rem;
      box-shadow: none;
      border: none;
      width: 100%;
      text-align: left;
      font-size: 1rem;
   }

   .plot-selector:hover {
      background: #e0e0e0;
      color: #403020;
   }

   :global(div.plot-container.off-screen) {
      position:absolute;
      top:0;
      left:50%;
      width: 650px;
      height: 600px;
      background: white;
      z-index: 999;
   }

   .plot {
      width: 100%;
      height: calc(100% - 15px);
   }

   .plot-select-msg {
      height: 15px;
      font-size: 12px;
      color: #aaa;
      text-align: center;
   }


   @media(min-width:1100px) {
      .plot-panel {
         grid-template-columns: 1fr minmax(285px, 30%);
      }
   }

   @media(min-width:1300px) {
      .plot-panel {
         grid-template-columns: 1fr max(300px, 30%);
      }
   }

   @media(min-width:1600px) {
      .plot-panel {
         grid-template-columns: 1fr 400px;
      }
   }

</style>